import { useCallback, useEffect, useMemo, useState } from 'react'

// Search, per-column filtering and sorting for every table in the console.
//
// Headless on purpose. The tables here render rich cells — status pills, email
// engagement, a tier badge beside a handle — and a generic "pass rows and
// columns, get a table" component would have flattened all of that to text. So
// a page keeps rendering its own <tr>, and this supplies the ordered, filtered
// rows plus the state the header controls bind to.
//
// A column says how to READ itself (`value`), not how to draw itself. That one
// function is what search, the filter list and sorting all use, so a column can
// never be searchable but unsortable by accident, and the value a filter offers
// is always the value the eye sees.

export type FilterKind = 'select' | 'text' | 'none'

export interface Column<T> {
  key: string
  label: string
  /** The cell's value for searching, filtering and sorting. */
  value?: (row: T) => string | number | null | undefined
  /** 'select' offers the distinct values; 'text' a contains box; 'none' neither. */
  filter?: FilterKind
  /** Numbers sort numerically and default to descending (biggest first). */
  numeric?: boolean
  sortable?: boolean
  className?: string
}

export type SortDir = 'asc' | 'desc'

export interface TableControls<T> {
  rows: T[]
  total: number
  search: string
  setSearch: (v: string) => void
  filters: Record<string, string[]>
  setFilter: (key: string, values: string[]) => void
  textFilters: Record<string, string>
  setTextFilter: (key: string, v: string) => void
  sort: { key: string; dir: SortDir } | null
  toggleSort: (key: string) => void
  distinct: (key: string) => string[]
  activeCount: number
  clearAll: () => void
  columns: Column<T>[]
}

const asText = (v: unknown): string =>
  v === null || v === undefined ? '' : typeof v === 'number' ? String(v) : String(v)

export function useTableControls<T>(
  rows: T[] | null | undefined,
  columns: Column<T>[],
  /** Namespaced so two tables on one page keep their own state. */
  storageKey?: string
): TableControls<T> {
  const all = useMemo(() => rows ?? [], [rows])

  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<Record<string, string[]>>({})
  const [textFilters, setTextFilters] = useState<Record<string, string>>({})
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(null)

  // Only the sort is remembered, and only per table. Restoring a filter would
  // mean opening the page to a subset with no sign that rows are missing —
  // which is how an operator concludes a table is empty when it is not.
  useEffect(() => {
    if (!storageKey) return
    try {
      const raw = localStorage.getItem(`plexii-admin.sort.${storageKey}`)
      if (raw) {
        const parsed = JSON.parse(raw) as { key: string; dir: SortDir }
        if (parsed && typeof parsed.key === 'string' && columns.some((c) => c.key === parsed.key)) {
          setSort(parsed)
        }
      }
    } catch {
      // storage unavailable (private window): the table just opens unsorted
    }
    // columns is a literal defined at module scope in every caller
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey])

  useEffect(() => {
    if (!storageKey) return
    try {
      if (sort) localStorage.setItem(`plexii-admin.sort.${storageKey}`, JSON.stringify(sort))
      else localStorage.removeItem(`plexii-admin.sort.${storageKey}`)
    } catch {
      // ignore
    }
  }, [sort, storageKey])

  const valueOf = useCallback(
    (row: T, col: Column<T>): string => asText(col.value ? col.value(row) : ''),
    []
  )

  const distinct = useCallback(
    (key: string): string[] => {
      const col = columns.find((c) => c.key === key)
      if (!col) return []
      const seen = new Set<string>()
      for (const r of all) {
        const v = valueOf(r, col).trim()
        seen.add(v === '' ? '—' : v)
      }
      return [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    },
    [all, columns, valueOf]
  )

  const setFilter = useCallback((key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
  }, [])

  const setTextFilter = useCallback((key: string, v: string) => {
    setTextFilters((prev) => {
      const next = { ...prev }
      if (!v.trim()) delete next[key]
      else next[key] = v
      return next
    })
  }, [])

  const toggleSort = useCallback(
    (key: string) => {
      const col = columns.find((c) => c.key === key)
      if (!col || col.sortable === false) return
      setSort((prev) => {
        if (!prev || prev.key !== key) return { key, dir: col.numeric ? 'desc' : 'asc' }
        if (prev.dir === (col.numeric ? 'desc' : 'asc')) return { key, dir: col.numeric ? 'asc' : 'desc' }
        return null // third click clears, back to the server's own order
      })
    },
    [columns]
  )

  const clearAll = useCallback(() => {
    setSearch('')
    setFilters({})
    setTextFilters({})
  }, [])

  const out = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = all

    if (q) {
      // Every space-separated word must appear somewhere in the row, so
      // "acme demo" finds the Acme demo request without caring about order.
      const words = q.split(/\s+/)
      list = list.filter((r) => {
        const hay = columns.map((c) => valueOf(r, c)).join(' \u0001 ').toLowerCase()
        return words.every((w) => hay.includes(w))
      })
    }

    for (const [key, values] of Object.entries(filters)) {
      const col = columns.find((c) => c.key === key)
      if (!col || values.length === 0) continue
      const want = new Set(values)
      list = list.filter((r) => {
        const v = valueOf(r, col).trim()
        return want.has(v === '' ? '—' : v)
      })
    }

    for (const [key, text] of Object.entries(textFilters)) {
      const col = columns.find((c) => c.key === key)
      if (!col) continue
      const t = text.trim().toLowerCase()
      list = list.filter((r) => valueOf(r, col).toLowerCase().includes(t))
    }

    if (sort) {
      const col = columns.find((c) => c.key === sort.key)
      if (col) {
        // A copy: the caller's array is the server's response and other parts
        // of the page still read it in arrival order.
        list = [...list].sort((a, b) => {
          const av = col.value ? col.value(a) : ''
          const bv = col.value ? col.value(b) : ''
          let cmp: number
          if (col.numeric) {
            const an = typeof av === 'number' ? av : parseFloat(asText(av).replace(/[^0-9.-]/g, ''))
            const bn = typeof bv === 'number' ? bv : parseFloat(asText(bv).replace(/[^0-9.-]/g, ''))
            const aa = Number.isFinite(an) ? an : -Infinity
            const bb = Number.isFinite(bn) ? bn : -Infinity
            cmp = aa - bb
          } else {
            cmp = asText(av).localeCompare(asText(bv), undefined, { numeric: true, sensitivity: 'base' })
          }
          return sort.dir === 'asc' ? cmp : -cmp
        })
      }
    }

    return list
  }, [all, columns, filters, textFilters, search, sort, valueOf])

  const activeCount =
    (search.trim() ? 1 : 0) + Object.keys(filters).length + Object.keys(textFilters).length

  return {
    rows: out,
    total: all.length,
    search,
    setSearch,
    filters,
    setFilter,
    textFilters,
    setTextFilter,
    sort,
    toggleSort,
    distinct,
    activeCount,
    clearAll,
    columns
  }
}
